/**
 * NBM Product Detail Page
 *
 * Handles:
 *   - Variant selection driven by option-value buttons
 *   - Price / stock / URL updates on variant change
 *   - Carousel navigation to the variant's featured image
 *   - AJAX Add to Cart with loading state
 *   - Recently Viewed products (localStorage)
 *
 * HTML contract (Liquid-rendered):
 *   [data-product-detail]                    — main product wrapper
 *   .js-variant-select[data-option-index][data-value]  — option value buttons
 *   [data-product-variants]                  — JSON script tag with variants array
 *   [data-carousel]                          — image carousel (auto-inited by carousel.js)
 *   [data-price-display]                      — price wrapper (contains .price snippet HTML)
 *   [data-stock-status]                      — stock status wrapper
 *   .js-add-to-cart[data-variant-id]         — Add to Cart buttons
 *   .product-detail__add-btn-text            — button text span
 */
(function () {
  'use strict';

  const RECENTLY_VIEWED_KEY = 'nbm-recently-viewed';
  const MAX_RECENTLY_VIEWED = 20;

  /* ------------------------------------------------------------------ */
  /*  Helpers                                                            */
  /* ------------------------------------------------------------------ */

  function getContainer() {
    return document.querySelector('[data-product-detail]');
  }

  /* ------------------------------------------------------------------ */
  /*  Variant matching                                                   */
  /* ------------------------------------------------------------------ */

  /** Read all currently active option values and return as {0: 'Red', 1: 'M', ...} */
  function getSelectedOptions(container) {
    const selections = {};
    const activeBtns = container.querySelectorAll('.js-variant-select.is-active');
    for (const btn of activeBtns) {
      const idx = parseInt(btn.getAttribute('data-option-index'), 10);
      selections[idx] = btn.getAttribute('data-value');
    }
    return selections;
  }

  /** Load parsed variants array from the hidden JSON script tag. */
  function getVariants(container) {
    const el = container.querySelector('[data-product-variants]');
    if (!el) return [];
    try { return JSON.parse(el.textContent) || []; } catch (_error) { return []; }
  }

  /**
   * Find the variant that matches all current selections.
   * @param {Object[]} variants
   * @param {Object}   selections  — { optionIndex: value }
   */
  function findMatchingVariant(variants, selections) {
    const count = Object.keys(selections).length;
    return variants.find((variant) => {
      for (let index = 0; index < count; index++) {
        if (variant[`option${index + 1}`] !== selections[index]) return false;
      }
      return true;
    }) || null;
  }

  /* ------------------------------------------------------------------ */
  /*  UI Updates                                                         */
  /* ------------------------------------------------------------------ */

  /** Update all .price wrappers inside a given element. */
  function updatePriceEl(el, price, comparePrice) {
    if (!el) return;

    const priceDiv = el.querySelector('.price');
    if (!priceDiv) return;

    const onSale = comparePrice && comparePrice > price;
    const pct = onSale ? Math.round((comparePrice - price) / comparePrice * 100) : 0;

    // Current price
    const currentEl = priceDiv.querySelector('.price__current');
    if (currentEl) {
      currentEl.textContent = window.NBMTheme.formatMoney(price);
      currentEl.classList.toggle('price__current--sale', onSale);
    }

    // Compare-at
    let compareEl = priceDiv.querySelector('.price__compare');
    if (onSale) {
      if (!compareEl) {
        compareEl = document.createElement('span');
        compareEl.className = 'price__compare';
        if (currentEl) currentEl.insertAdjacentElement('afterend', compareEl);
        else priceDiv.appendChild(compareEl);
      }
      compareEl.textContent = window.NBMTheme.formatMoney(comparePrice);
      compareEl.hidden = false;
    } else if (compareEl) {
      compareEl.hidden = true;
    }

    // Discount badge
    let badgeEl = priceDiv.querySelector('.price__badge');
    if (onSale) {
      if (!badgeEl) {
        badgeEl = document.createElement('span');
        badgeEl.className = 'price__badge';
        if (compareEl) compareEl.insertAdjacentElement('afterend', badgeEl);
        else priceDiv.appendChild(badgeEl);
      }
      badgeEl.textContent = `-${pct}%`;
      badgeEl.hidden = false;
    } else if (badgeEl) {
      badgeEl.hidden = true;
    }
  }

  /** Update the stock status block. */
  function updateStock(container, variant) {
    const el = container.querySelector('[data-stock-status]');
    if (!el) return;

    el.innerHTML = '';
    const span = document.createElement('span');

    if (!variant.available) {
      span.className = 'product-detail__stock-text product-detail__stock-text--out';
      span.textContent = 'Out of stock';
    } else if (
      variant.inventory_management === 'shopify' &&
      variant.inventory_policy === 'deny' &&
      variant.inventory_quantity <= 3 &&
      variant.inventory_quantity > 0
    ) {
      span.className = 'product-detail__stock-text product-detail__stock-text--low';
      span.textContent = `Only ${variant.inventory_quantity} left`;
    } else {
      span.className = 'product-detail__stock-text product-detail__stock-text--in-stock';
      span.textContent = 'In Stock';
    }

    el.appendChild(span);
  }

  /** Update all Add to Cart buttons. */
  function updateAddToCartBtns(container, variant) {
    const btns = container.querySelectorAll('.js-add-to-cart');
    for (const btn of btns) {
      btn.setAttribute('data-variant-id', variant.id);
      btn.disabled = !variant.available;
      const textEl = btn.querySelector('.product-detail__add-btn-text');
      if (textEl) textEl.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
    }
  }

  /** Navigate the carousel to the slide matching the variant's featured image. */
  function scrollToVariantImage(container, variant) {
    if (!variant.featured_image) return;

    const carousel = container.querySelector('[data-carousel]');
    if (!carousel) return;

    const carouselInstance = carousel._nbmCarousel;
    const slides = carousel.querySelectorAll('.carousel__item');

    // Extract bare filename (without size suffix / query string) for matching
    const variantSrc = variant.featured_image.src || '';
    const variantFile = variantSrc.split('/').pop().split('?')[0].replace(/(_\d+x\d*)+/, '');

    for (let index = 0; index < slides.length; index++) {
      const img = slides[index].querySelector('img');
      if (!img || !img.src) continue;

      const slideFile = img.src.split('/').pop().split('?')[0].replace(/(_\d+x\d*)+/, '');
      if (slideFile === variantFile) {
        if (carouselInstance) {
          carouselInstance.goTo(index);
        } else {
          slides[index].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        }
        break;
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Variant Selection                                                  */
  /* ------------------------------------------------------------------ */

  function handleVariantClick(btn) {
    const container = getContainer();
    if (!container) return;

    const optionIndex = btn.getAttribute('data-option-index');
    const value = btn.getAttribute('data-value');

    // Toggle active within the same option group only
    const groupBtns = container.querySelectorAll(`.js-variant-select[data-option-index="${optionIndex}"]`);
    for (const b of groupBtns) {
      b.classList.toggle('is-active', b.getAttribute('data-value') === value);
    }

    // Update selected value label
    const optionGroup = btn.closest('.variant-option');
    if (optionGroup) {
      const selectedEl = optionGroup.querySelector('.variant-option__selected');
      if (selectedEl) selectedEl.textContent = ' ' + value;
    }

    // Resolve matching variant
    const selections = getSelectedOptions(container);
    const variants = getVariants(container);
    const variant = findMatchingVariant(variants, selections);
    if (!variant) return;

    // Price — main block and mobile sticky bar
    updatePriceEl(container.querySelector('[data-price-display]'), variant.price, variant.compare_at_price);
    updatePriceEl(container.querySelector('[data-sticky-price]'), variant.price, variant.compare_at_price);

    // Stock
    updateStock(container, variant);

    // Add to Cart
    updateAddToCartBtns(container, variant);

    // Wishlist — update variant_id so heart reflects the currently selected variant
    const wishlistBtn = container.querySelector('.js-wishlist-toggle');
    if (wishlistBtn) {
      wishlistBtn.setAttribute('data-product-variant-id', variant.id);
      if (window.NBMWishlist) window.NBMWishlist.updateAllIcons();
    }

    // URL (no reload)
    if (window.history && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    // Carousel
    scrollToVariantImage(container, variant);
  }

  /* ------------------------------------------------------------------ */
  /*  Add to Cart                                                        */
  /* ------------------------------------------------------------------ */

  function handleAddToCart(btn) {
    if (btn.disabled) return;

    const variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    btn.disabled = true;
    btn.classList.add('is-loading');
    const textEl = btn.querySelector('.product-detail__add-btn-text');
    const originalText = textEl ? textEl.textContent : '';
    if (textEl) textEl.textContent = 'Adding…';

    const addFn = window.NBMTheme && window.NBMTheme.addToCart
      ? window.NBMTheme.addToCart
      : fallbackAddToCart;

    addFn(variantId, 1)
      .catch(() => { /* error toast handled inside */ })
      .finally(() => {
        btn.disabled = false;
        btn.classList.remove('is-loading');
        if (textEl) textEl.textContent = originalText;
      });
  }

  function fallbackAddToCart(variantId) {
    const url = (window.NBMRoutes && window.NBMRoutes.cartAdd) || '/cart/add.js';
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: `id=${parseInt(variantId, 10)}&quantity=1`
    }).then((res) => res.json().then((data) => {
      if (!res.ok) throw new Error((data && (data.description || data.message)) || 'Add to cart failed');
      return data;
    }));
  }

  /* ------------------------------------------------------------------ */
  /*  Recently Viewed                                                    */
  /* ------------------------------------------------------------------ */

  function saveRecentlyViewed() {
    const container = getContainer();
    if (!container) return;

    const id = container.getAttribute('data-product-id');
    const handle = container.getAttribute('data-product-handle');
    if (!id || !handle) return;

    let items;
    try { items = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY)) || []; }
    catch (e) { items = []; }

    items = items.filter((p) => String(p.id) !== String(id));
    items.unshift({ id: String(id), handle });
    if (items.length > MAX_RECENTLY_VIEWED) items = items.slice(0, MAX_RECENTLY_VIEWED);

    try { localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(items)); }
    catch (e) { /* storage full */ }
  }

  /* ------------------------------------------------------------------ */
  /*  Event Delegation                                                   */
  /* ------------------------------------------------------------------ */

  function getCarousel() {
    const el = document.querySelector('[data-product-detail] [data-carousel]');
    return el && el._nbmCarousel ? el._nbmCarousel : null;
  }

  document.addEventListener('click', (event) => {
    const variantBtn = event.target.closest('.js-variant-select');
    if (variantBtn) {
      event.preventDefault();
      handleVariantClick(variantBtn);
      return;
    }

    if (event.target.closest('.js-carousel-prev')) {
      const carousel = getCarousel();
      if (carousel) carousel.prev();
      return;
    }

    if (event.target.closest('.js-carousel-next')) {
      const carousel = getCarousel();
      if (carousel) carousel.next();
      return;
    }

    const addBtn = event.target.closest('.js-add-to-cart');
    if (addBtn) {
      event.preventDefault();
      handleAddToCart(addBtn);
    }
  });

  /* ------------------------------------------------------------------ */
  /*  Recommendations                                                    */
  /* ------------------------------------------------------------------ */

  function initRecommendations() {
    var el = document.querySelector('[data-recommendations]');
    if (!el) return;

    var productId = el.getAttribute('data-product-id');
    var limit = el.getAttribute('data-limit') || '8';
    var track = el.querySelector('[data-recommendations-track]');
    if (!productId || !track) return;

    var url = '/recommendations/products?product_id=' + productId +
              '&limit=' + limit +
              '&intent=related' +
              '&section_id=product-recommendations';

    fetch(url)
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var items = doc.querySelector('.recommendations-items');
        if (!items || !items.children.length) { el.hidden = true; return; }

        track.innerHTML = items.innerHTML;

        // Arrow + dots nav — shared utility
        var prevBtn = el.querySelector('.rv-arrow--prev');
        var nextBtn = el.querySelector('.rv-arrow--next');
        var wrap = el.querySelector('.related-products__carousel-wrap');
        var dotsEl = document.createElement('div');
        dotsEl.className = 'rv-dots';
        if (wrap) wrap.appendChild(dotsEl);

        if (window.NBMTheme && window.NBMTheme.initCarousel) {
          window.NBMTheme.initCarousel({ track: track, prevBtn: prevBtn, nextBtn: nextBtn, dotsContainer: dotsEl });
        }

        // Sync wishlist active state
        if (window.NBMWishlist) {
          var ids = window.NBMWishlist.getAll().map(function (p) { return String(p.id || p); });
          el.querySelectorAll('.js-wishlist-toggle').forEach(function (btn) {
            if (ids.indexOf(btn.getAttribute('data-product-id')) !== -1) {
              btn.classList.add('is-active');
            }
          });
        }
      })
      .catch(function () { el.hidden = true; });
  }

  /* ------------------------------------------------------------------ */
  /*  Init                                                               */
  /* ------------------------------------------------------------------ */

  function initVariantSwatches() {
    // Search filter
    document.querySelectorAll('[data-variant-search]').forEach((input) => {
      const container = input.closest('.variant-option').querySelector('[data-swatch-container]');
      if (!container) return;
      input.addEventListener('input', () => {
        const query = input.value.toLowerCase().trim();
        container.querySelectorAll('.variant-swatch').forEach((btn) => {
          const name = (btn.getAttribute('title') || '').toLowerCase();
          btn.hidden = !!(query && !name.includes(query));
        });
      });
    });

    // Show more toggle
    document.querySelectorAll('[data-show-more]').forEach((btn) => {
      const container = btn.closest('.variant-option').querySelector('[data-swatch-container]');
      if (!container) return;
      btn.addEventListener('click', () => {
        container.querySelectorAll('.variant-swatch[hidden]').forEach((swatch) => swatch.removeAttribute('hidden'));
        btn.hidden = true;
      });
    });
  }

  function init() {
    saveRecentlyViewed();
    initRecommendations();
    initVariantSwatches();
    // Carousel is auto-initialised by carousel.js — no duplicate init needed
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
